import { Nullable } from 'cng-common/models/utils'

import { CampaignObjective } from '../../model/campaign'
import { campaignBaseApi } from '../campaignBaseApi'
import { mapObjectiveFromRawData } from '../mapping'
import {
  CampaignApiTags,
  CreateCampaignObjectiveParams,
  DeleteCampaignObjectiveParams,
  GetCampaignObjectivesParams,
  UpdateCampaignObjectiveParams,
} from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CAMPAIGN_OBJECTIVES_CREATE,
  SP_LUT_CAMPAIGN_OBJECTIVES_DELETE,
  SP_LUT_CAMPAIGN_OBJECTIVES_GET,
  SP_LUT_CAMPAIGN_OBJECTIVES_UPDATE,
} from '../procedures'
import { mapFirstRow } from '../responseMappers'

const objectivesEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Fetch campaign objectives
    // This endpoint retrieves the objectives associated with a specific campaign.
    // It allows users to see the goals set for the campaign.
    getCampaignObjectives: build.query<
      { items: CampaignObjective[]; total: number },
      GetCampaignObjectivesParams
    >({
      transformResponse(response) {
        return {
          items: response.Data.map(mapObjectiveFromRawData).sort((a, b) => b.entryId - a.entryId),
          total: response.Total,
        }
      },

      providesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignObjectives, id: params.campaignId },
      ],

      query({ campaignId }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_OBJECTIVES_GET),
          method: 'POST',
          data: {
            'p.CampaignId': campaignId,
          },
        }
      },
    }),

    // Create campaign objective
    // This endpoint creates a new campaign objective.
    createCampaignObjective: build.mutation<
      Nullable<CampaignObjective>,
      CreateCampaignObjectiveParams
    >({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_OBJECTIVES_CREATE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.Title': params.title,
            'p.Content': params.content,
            'p.Countries': JSON.stringify(params.countries),
            'p.CampaignId': params.campaignId,
            'p.IsVisible': params.isVisible ? 1 : 0,
          },
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignObjectives, id: params.campaignId },
      ],

      transformResponse: mapFirstRow(mapObjectiveFromRawData),
    }),

    // Update campaign objective
    // This endpoint updates the details of a specific campaign objective.
    updateCampaignObjective: build.mutation<
      Nullable<CampaignObjective>,
      UpdateCampaignObjectiveParams
    >({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_OBJECTIVES_UPDATE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.ID': params.entryId,
            'p.Content': params.content,
            'p.Countries': JSON.stringify(params.countries),
            'p.IsVisible': params.isVisible ? 1 : 0,
            'p.Title': params.title,
          },
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignObjectives, id: params.campaignId },
      ],

      transformResponse: mapFirstRow(mapObjectiveFromRawData),
    }),

    // Delete campaign objective
    // This endpoint deletes a specific campaign objective.
    deleteCampaignObjective: build.mutation<
      Nullable<CampaignObjective>,
      DeleteCampaignObjectiveParams
    >({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_OBJECTIVES_DELETE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.ID': params.entryId,
          },
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignObjectives, id: params.campaignId },
      ],

      transformResponse: mapFirstRow(mapObjectiveFromRawData),
    }),
  }),
})

export const {
  useGetCampaignObjectivesQuery,
  useCreateCampaignObjectiveMutation,
  useUpdateCampaignObjectiveMutation,
  useDeleteCampaignObjectiveMutation,
} = objectivesEndpoints
